import { CloseOutlined, EyeInvisibleOutlined, LoadingOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { CPH_FLAG, RESOLVE_FLAG_TYPES, hrStyle, rowStyleGray, rowStyles } from '@components/_SiteWide/Flags/constants'
import { SingleFlag } from '@components/_SiteWide/Flags/FlagIcon'
import { ResolveFlagForm } from '@components/_SiteWide/Flags/ResolveFlagForm'
import { useOrderDispatchRowPinning } from '@components/OrderDispatch/OrderDispatch'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { isDefined, ReadableDifference } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Col, Row, Table } from 'antd'
import moment from 'moment'
import { useEffect, useState } from 'react'
import api from '@utils/api'
import { useQuery } from '@tanstack/react-query'
import { formatValue, getTableColumns, getTableDataSource } from './util'
import './styles.css'
import { useModelConfig } from '@/hooks/useModelConfig'

type PopoverContentProps = {
  flag: Gravitate.OrderFlag
  orderId: string
  setIsVisible: (visible: boolean) => void
  updateOrder: (order: UpdateOrderPayload) => void
  updateGridOrder?: (order: Gravitate.Order) => void
  isVisible: boolean
  isOrderFlag?: boolean
  allowFlagResolve?: boolean
}

type UpdateOrderPayload = {
  order_id: string
  flag_type: string
  reason_code: string | null
  notes: string | null
}

export function PopoverContent({
  flag,
  orderId,
  setIsVisible,
  updateOrder,
  isVisible,
  updateGridOrder,
  isOrderFlag = false,
  allowFlagResolve = true,
}: PopoverContentProps) {
  const [reason, setReason] = useState(null)
  const [notes, setNotes] = useState('')
  const [showResolveForm, setShowResolveForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const { formatCurrency, formatDistance } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  const formattedDistance = formatDistance(null, false)

  const { data: reasonCodes } = useQuery({
    queryKey: ['orderFlagReasonCodes'],
    queryFn: async () => {
      try {
        const resp = await api.getFlagReasonCodes()
        return resp.data
      } catch (error) {
        console.error('Error fetching reason codes:', error)
        return { supply_options: [], counterparty_hold_removal: [] }
      }
    },
  })

  useEffect(() => {
    if (!isVisible) setShowResolveForm(false)
  }, [isVisible])

  const { rowPinningApiRef } = useOrderDispatchRowPinning()

  async function resolveFlag(clearResolution = false) {
    if (!loading) setLoading(true)

    let reasonCode = null
    if (!clearResolution) {
      // reason code will always be the same for cph flag, send in null to clear resolve
      reasonCode = flag.type === CPH_FLAG ? reasonCodes?.counterparty_hold_removal[0].reason : reason
    }

    const payload = {
      order_id: orderId,
      flag_type: flag.type,
      reason_code: reasonCode,
      notes,
    }
    const res = await api.updateOrderFlag(payload)
    if (res.status === 200) {
      const { data: order } = await api.getOrderById(orderId)

      if (!order.unresolved_flags) {
        order.unresolved_flags = order.flags?.filter((f: Gravitate.OrderFlag) => f && f.reason_code === null) ?? []
      }
      if (!order.resolved_flag) {
        order.resolved_flag = order.flags?.filter((f: Gravitate.OrderFlag) => f && f.reason_code !== null) ?? []
      }
      updateOrder(order)
      if (updateGridOrder) updateGridOrder(order)

      if (rowPinningApiRef.current && rowPinningApiRef.current.pinnedRowData?.top?.length) {
        const pinnedIndex = rowPinningApiRef.current.pinnedRowData.top.findIndex((row) => row.number === order.number)

        if (pinnedIndex !== -1) {
          const pin = rowPinningApiRef.current.pinnedRowData.top[pinnedIndex]
          Object.keys(pin).forEach((key) => {
            pin[key] = order[key]
          })
        }
      }
    }
    setLoading(false)
    setIsVisible(false)
    setShowResolveForm(false)
  }

  if (showResolveForm)
    return (
      <ResolveFlagForm
        flag={flag}
        reason={reason}
        setReason={setReason}
        notes={notes}
        setNotes={setNotes}
        setShowResolveForm={setShowResolveForm}
        showResolveForm={showResolveForm}
        resolveFlag={resolveFlag}
        loading={loading}
        setLoading={setLoading}
        reasonCodes={reasonCodes.supply_options || []}
      />
    )

  if (flag.reason_code !== null) {
    return (
      <ResolvedFlag
        flag={flag}
        resolveFlag={resolveFlag}
        loading={loading}
        setLoading={setLoading}
        formattedCurrency={formattedCurrency}
        formattedDistance={formattedDistance}
      />
    )
  }

  return (
    <Vertical style={{ borderBottom: '1px solid var(--gray-200)' }} className='pb-2'>
      <Horizontal verticalCenter className='pt-2' style={rowStyles} gap={8}>
        <SingleFlag small flag={flag} isOrderFlag={isOrderFlag} />
        <Texto weight='bold' className='ml-3 flex-1' style={{ flexWrap: 'nowrap' }} appearance={flag.severity}>
          {flag.name}
        </Texto>
        {RESOLVE_FLAG_TYPES.includes(flag.type) && allowFlagResolve && (
          <GraviButton
            onClick={() => setShowResolveForm(!showResolveForm)}
            icon={<EyeInvisibleOutlined />}
            style={{ color: 'var(--theme-color-2)' }}
            buttonText='Resolve'
            appearance='outline'
          />
        )}
      </Horizontal>
      <FlagDetails flag={flag} />
    </Vertical>
  )
}
function FlagDetails({ flag }: { flag: Gravitate.OrderFlag }) {
  const formatterHook = useFormatter()
  const { config } = useModelConfig()
  const details = flag.sub_flags?.length > 0 ? flag.sub_flags.map((subFlag) => subFlag.details) : [flag.details] // aggregate child flags if they exist

  if (details.filter(Boolean).length === 0 || details[0].length === 0) return null // if no details are present, return null

  if (details.length === 1 && details[0].length === 1) {
    return <SingleFlagDetail row={details[0][0]} flagType={flag.type} formatterHook={formatterHook} />
  }

  const tableCols = getTableColumns(details, config.sales_variance_percent_threshold)
  const tableData = getTableDataSource(details, formatterHook)
  return (
    <Table
      columns={tableCols}
      dataSource={tableData}
      pagination={false}
      className='flag-details-table'
      rowHoverable={false}
    />
  )
}

function SingleFlagDetail({
  row,
  flagType,
  formatterHook,
}: {
  row: Gravitate.OrderFlagDetail
  flagType: Gravitate.OrderFlag['type']
  formatterHook: ReturnType<typeof useFormatter>
}) {
  const formattedValue = formatValue(row.label, row.value, formatterHook)

  return (
    <Horizontal gap={8}>
      <Texto weight={600} appearance='medium' style={{ whiteSpace: 'nowrap' }}>
        {row.label === 'Loaded Mile Difference' ? 'Terminal Distance' : row.label}
      </Texto>
      {row.date && (
        <Texto>
          {flagType === 'recent_drop' || flagType === 'compliance_hold' ? `${formatters.DATE_TIME(row.date)} ` : null}
          {flagType !== 'compliance_hold' ? (
            <b>{ReadableDifference(row.date, undefined, flagType === 'run_out')}</b>
          ) : null}
        </Texto>
      )}
      {formattedValue && <Texto category='p1'>{formattedValue}</Texto>}
    </Horizontal>
  )
}

type ResolvedFlagProps = {
  flag: Gravitate.OrderFlag
  resolveFlag: (clearResolution?: boolean) => void
  loading: boolean
  setLoading: (loading: boolean) => void
  formattedCurrency: string
  formattedDistance: string
}

function ResolvedFlag({ flag, resolveFlag, loading, formattedCurrency, formattedDistance }: ResolvedFlagProps) {
  return (
    <>
      <Horizontal style={rowStyles} alignItems='center' className='py-1'>
        <SingleFlag small flag={flag} style={{ opacity: 0.5 }} />
        <Col style={{ justifyContent: 'flex-start', flexWrap: 'wrap' }} className='flex-div' flex={1}>
          <Texto
            align='left'
            weight='bold'
            className='ml-3'
            style={{ flexWrap: 'wrap', opacity: 0.5 }}
            appearance={flag.severity}
          >
            {flag.name} <Texto style={{ display: 'inline', marginLeft: 5 }}>(Resolved)</Texto>
            <Texto>By {flag.updated_by}</Texto>
          </Texto>
        </Col>

        <Col>
          <GraviButton
            disabled={loading}
            onClick={() => resolveFlag(true)}
            icon={loading ? <LoadingOutlined spin /> : <CloseOutlined />}
            style={{ color: 'var(--theme-color-3)' }}
            buttonText='Clear Resolution'
            appearance='outline'
            className='ml-4'
          />
        </Col>
      </Horizontal>
      <hr style={hrStyle} />
      {flag.type !== CPH_FLAG && (
        <Row style={rowStyles}>
          <Col className='flex-div' flex={1}>
            <Texto appearance='medium' weight={600}>
              Reason Code
            </Texto>
          </Col>
          <Col className='flex-div'>{flag.reason_code === '' ? 'N/A' : flag.reason_code}</Col>
        </Row>
      )}
      <Row style={rowStyles}>
        <Col className='flex-div' flex={1}>
          <Texto appearance='medium' weight={600}>
            Note
          </Texto>
        </Col>
        <Col className='flex-div'>
          <Texto> {flag.notes === null ? 'N/A' : flag.notes}</Texto>
        </Col>
      </Row>
      <Row style={rowStyleGray}>
        {flag.details?.length > 0 && (
          <Col className='flex-div' style={{ justifyContent: 'space-between' }} flex={1}>
            {flag.details &&
              flag.details.map((d, i) => {
                const showPrice = d.value && d.label.toLowerCase().includes('price')
                const showDistance = d.value && d.label.toLowerCase().includes('distance')
                const showValue =
                  d.value && !d.label.toLowerCase().includes('distance') && !d.label.toLowerCase().includes('price')

                return (
                  <Col key={i}>
                    <Texto weight={600} appearance='medium'>
                      {d.label === 'Loaded Mile Difference' ? 'Terminal Distance' : d.label}
                    </Texto>
                    {d.date && (
                      <p>
                        {flag.type === 'recent_drop' ? (
                          <span>{`${moment(d.date).format('L LT')} ${moment()
                            .tz(moment.tz.guess())
                            .format('z')}`}</span>
                        ) : null}
                        <b> ({ReadableDifference(moment(d.date), undefined, flag.type === 'run_out')})</b>
                      </p>
                    )}
                    {(showPrice || showDistance || showValue) && (
                      <p>
                        {showPrice && `${formattedCurrency}${d.value}`}
                        {showDistance &&
                          (d.value < 0
                            ? `${Math.abs(d.value)} ${formattedDistance} further`
                            : `${Math.abs(d.value)} ${formattedDistance} closer`)}
                        {showValue && d.value}
                      </p>
                    )}
                  </Col>
                )
              })}
          </Col>
        )}
      </Row>
    </>
  )
}
